import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ClipboardList, Menu, Search, X, ArrowUpRight } from "lucide-react";
import { NAV } from "@/data/site";
import { useQuote } from "@/context/QuoteContext";
import { getProducts } from "@/lib/api";
import { EASE } from "@/components/Reveal";

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const { count, setOpen } = useQuote();
  const navigate = useNavigate();

  useEffect(() => {
    if (!searchOpen) return;
    const t = setTimeout(() => {
      getProducts(q ? { q } : {}).then((r) => setResults(r.slice(0, 6)));
    }, 220);
    return () => clearTimeout(t);
  }, [q, searchOpen]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed inset-x-0 top-0 z-40 border-b border-ink/10 bg-cream/90 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-6 px-5 py-3 md:px-10">
          <Link to="/" data-testid="header-logo-link" className="flex shrink-0 items-center">
            <img
              src="/mr-kwila-logo.png"
              alt="Mr Kwila — Timber, Doors & Furniture"
              className="h-11 w-auto mix-blend-multiply md:h-14"
            />
          </Link>

          <nav className="hidden items-center gap-6 xl:flex" data-testid="header-nav">
            {NAV.slice(0, 8).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-[13px] font-semibold uppercase tracking-[0.14em] transition-colors hover:text-kwila ${
                    isActive ? "text-kwila" : "text-ink/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              data-testid="search-toggle-button"
              onClick={() => setSearchOpen((v) => !v)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-ink transition-colors hover:bg-ink hover:text-cream"
              aria-label="Search products"
            >
              {searchOpen ? <X size={17} /> : <Search size={17} />}
            </button>
            <button
              data-testid="quote-list-button"
              onClick={() => setOpen(true)}
              className="relative flex h-10 items-center gap-2 rounded-full bg-ink px-4 text-cream transition-colors hover:bg-kwila"
            >
              <ClipboardList size={16} />
              <span className="hidden text-[13px] font-semibold uppercase tracking-[0.12em] md:inline">
                Quote List
              </span>
              {count > 0 && (
                <span
                  data-testid="quote-list-count"
                  className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-kwila text-[11px] font-bold text-cream ring-2 ring-cream"
                >
                  {count}
                </span>
              )}
            </button>
            <button
              data-testid="mobile-menu-button"
              onClick={() => setMenuOpen(true)}
              className="flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 xl:hidden"
              aria-label="Open menu"
            >
              <Menu size={18} />
            </button>
          </div>
        </div>

        <AnimatePresence>
          {searchOpen && (
            <motion.div
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.3, ease: EASE }}
              className="border-t border-ink/10 bg-cream px-5 py-6 md:px-10"
              data-testid="search-panel"
            >
              <div className="mx-auto max-w-[1500px]">
                <input
                  data-testid="search-input"
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search tables, chairs, doors, timber…"
                  className="w-full border-b border-ink/20 bg-transparent pb-3 font-display text-2xl outline-none placeholder:text-sand/60 focus:border-kwila md:text-3xl"
                />
                {results.length > 0 && (
                  <div className="mt-4 grid gap-2 md:grid-cols-2">
                    {results.map((r) => (
                      <button
                        key={r.id}
                        data-testid={`search-result-${r.slug}`}
                        onClick={() => {
                          setSearchOpen(false);
                          navigate(`/product/${r.slug}`);
                        }}
                        className="group flex items-center justify-between border-b border-ink/10 py-3 text-left"
                      >
                        <div>
                          <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-sand">
                            {r.division} · {r.category}
                          </p>
                          <p className="font-display text-lg leading-tight">{r.name}</p>
                        </div>
                        <ArrowUpRight size={16} className="text-sand transition-colors group-hover:text-kwila" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
            className="fixed inset-0 z-50 flex flex-col bg-ink text-cream"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 py-4 md:px-10">
              <span className="font-display text-2xl tracking-wide">MR ~ KWILA</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMenuOpen(false)}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-cream/25"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-1 px-8 md:px-16">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.5, ease: EASE }}
                >
                  <Link
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    onClick={() => setMenuOpen(false)}
                    className="group flex items-baseline gap-4 border-b border-cream/10 py-3"
                  >
                    <span className="text-xs font-bold text-kwila">0{i + 1}</span>
                    <span className="font-display text-4xl transition-colors group-hover:text-kwila md:text-5xl">
                      {item.label}
                    </span>
                  </Link>
                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.6 }}
                className="pt-8"
              >
                <Link
                  to="/request-quote"
                  data-testid="mobile-nav-request-quote"
                  onClick={() => setMenuOpen(false)}
                  className="inline-flex items-center gap-2 rounded-full bg-kwila px-8 py-3 text-sm font-bold uppercase tracking-[0.14em] text-cream"
                >
                  Request a Quote <ArrowUpRight size={16} />
                </Link>
              </motion.div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
